<template>
  <FlashNotifications ref="flash" />

  <!-- ═══════════════ MOBILE ═══════════════ -->
  <div class="sm:hidden bg-gray-50 dark:bg-[#0d1117] -mx-5 pt-4 pb-20">

    <!-- Object header: identity -->
    <div class="flex flex-col items-center px-4 pb-2">
      <div class="relative">
        <AvatarUploadCard
          ref="mobileAvatarCard"
          :picture-url="org.profile_picture_url"
          :avatar-name="org.avatar_name"
          :avatar-color-class="org.avatar_color"
          :upload-url="`/api/v1/organizations/${org.id}/profile_picture`"
          :remove-url="`/api/v1/organizations/${org.id}/remove_profile_picture`"
          form-field-name="organization[profile_picture]"
          title="Change profile picture"
          shape="rounded"
          @updated="onAvatarUpdated"
          @error="onAvatarError"
        />
        <button type="button" @click="mobileAvatarCard?.openModal()"
          class="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-white dark:bg-[#1C2128] border border-gray-200 dark:border-gray-600 shadow-sm flex items-center justify-center text-gray-500 dark:text-gray-400">
          <svg class="w-3 h-3" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125"/>
          </svg>
        </button>
      </div>
      <p class="mt-3 text-base font-bold text-gray-900 dark:text-white leading-snug">{{ org.name || 'Your organization' }}</p>
      <a v-if="orgUrl" :href="'/' + orgUrl" target="_blank"
         class="mt-1 text-xs text-[#0070F2] dark:text-blue-400 font-mono">
        {{ APPLICATION_DOMAIN }}/{{ orgUrl }}
      </a>
    </div>

    <!-- Identity -->
    <p class="px-4 pt-6 pb-2 text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Identity</p>
    <div class="bg-white dark:bg-[#1C2128] rounded-2xl mx-4 overflow-hidden divide-y divide-gray-100 dark:divide-gray-700/50">
      <div class="px-4 py-3">
        <label class="block text-[10px] font-medium text-gray-400 dark:text-gray-500 mb-1">Organization name</label>
        <input :value="org.name"
               @focusout="updateOrgField"
               name="organization[name]" type="text"
               placeholder="Acme Inc."
               class="w-full text-sm text-gray-900 dark:text-gray-100 bg-transparent focus:outline-none placeholder-gray-300 dark:placeholder-gray-600" />
      </div>
      <div class="px-4 py-3">
        <label class="block text-[10px] font-medium text-gray-400 dark:text-gray-500 mb-1">Organization URL</label>
        <div class="flex items-center gap-1">
          <span class="text-xs text-gray-400 dark:text-gray-500 font-mono flex-shrink-0">{{ APPLICATION_DOMAIN }}/</span>
          <input :value="orgUrl"
                 @input="validateURL"
                 @focusout="updateOrgField"
                 name="organization[url]" type="text" placeholder="your-org"
                 class="flex-1 text-sm text-gray-900 dark:text-gray-100 bg-transparent focus:outline-none font-mono placeholder-gray-300 dark:placeholder-gray-600 min-w-0" />
        </div>
        <p v-if="errors.url" class="mt-1 text-xs text-[#EE3939]">{{ errors.url }}</p>
      </div>
    </div>

    <!-- About -->
    <p class="px-4 pt-6 pb-2 text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">About</p>
    <div class="bg-white dark:bg-[#1C2128] rounded-2xl mx-4 overflow-hidden">
      <div class="px-4 py-3">
        <label class="block text-[10px] font-medium text-gray-400 dark:text-gray-500 mb-1">Bio</label>
        <textarea :value="org.bio"
                  @focusout="updateOrgField"
                  name="organization[bio]" rows="4"
                  placeholder="Tell people what your organization does"
                  class="w-full text-sm text-gray-900 dark:text-gray-100 bg-transparent focus:outline-none resize-none placeholder-gray-300 dark:placeholder-gray-600 leading-relaxed"></textarea>
      </div>
    </div>

    <!-- Preferences -->
    <p class="px-4 pt-6 pb-2 text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Preferences</p>
    <div class="bg-white dark:bg-[#1C2128] rounded-2xl mx-4 overflow-hidden divide-y divide-gray-100 dark:divide-gray-700/50">
      <div class="px-4 py-3.5 flex items-center justify-between gap-4">
        <div class="flex-1 min-w-0">
          <p class="text-sm font-medium text-gray-900 dark:text-gray-100 leading-snug">{{ I18N.website_content.account_settings.show_msg_btn_on_profile_page }}</p>
          <p class="text-xs text-gray-400 dark:text-gray-500 mt-0.5 leading-snug">{{ I18N.website_content.account_settings.subheading1 }}</p>
        </div>
        <ToggleSwitch :model-value="org.show_message_button_on_profile" @change="togglePreference('show_message_button_on_profile', $event)" />
      </div>
      <div class="px-4 py-3.5 flex items-center justify-between gap-4">
        <div class="flex-1 min-w-0">
          <p class="text-sm font-medium text-gray-900 dark:text-gray-100 leading-snug">{{ I18N.website_content.account_settings.show_msg_btn_on_events_page }}</p>
          <p class="text-xs text-gray-400 dark:text-gray-500 mt-0.5 leading-snug">{{ I18N.website_content.account_settings.subheading2 }}</p>
        </div>
        <ToggleSwitch :model-value="org.show_message_button_on_events" @change="togglePreference('show_message_button_on_events', $event)" />
      </div>
    </div>

    <!-- Social links -->
    <p class="px-4 pt-6 pb-2 text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Social links</p>
    <router-link :to="`/organizations/${org.url}/profile/social_links`" data-turbo-prefetch="false"
      class="mx-4 flex items-center gap-3 bg-white dark:bg-[#1C2128] rounded-2xl overflow-hidden px-4 py-3.5">
      <div class="w-9 h-9 rounded-lg bg-[#EBF5FF] dark:bg-blue-900/20 flex items-center justify-center flex-shrink-0">
        <svg class="w-4 h-4 text-[#0070F2] dark:text-blue-400" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M13.19 8.688a4.5 4.5 0 0 1 1.242 7.244l-4.5 4.5a4.5 4.5 0 0 1-6.364-6.364l1.757-1.757m13.35-.622 1.757-1.757a4.5 4.5 0 0 0-6.364-6.364l-4.5 4.5a4.5 4.5 0 0 0 1.242 7.244"/>
        </svg>
      </div>
      <div class="flex-1 min-w-0">
        <p class="text-sm font-semibold text-gray-900 dark:text-gray-100 leading-snug">Manage social links</p>
        <p class="text-xs text-gray-400 dark:text-gray-500 mt-0.5">Website, social profiles, and visibility</p>
      </div>
      <svg class="w-4 h-4 text-gray-300 dark:text-gray-600 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" d="m9 18 6-6-6-6"/>
      </svg>
    </router-link>
  </div>

  <!-- ═══════════════ DESKTOP ═══════════════ -->
  <div class="hidden sm:block">

    <!-- Object header: identity -->
    <div class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 mb-4 flex items-center justify-between gap-4 px-6 py-4">
      <div class="flex items-center gap-4 min-w-0">
        <div class="relative flex-shrink-0">
          <AvatarUploadCard
            ref="desktopAvatarCard"
            :picture-url="org.profile_picture_url"
            :avatar-name="org.avatar_name"
            :avatar-color-class="org.avatar_color"
            :upload-url="`/api/v1/organizations/${org.id}/profile_picture`"
            :remove-url="`/api/v1/organizations/${org.id}/remove_profile_picture`"
            form-field-name="organization[profile_picture]"
            title="Change profile picture"
            shape="rounded"
            @updated="onAvatarUpdated"
            @error="onAvatarError"
          />
          <button type="button" @click="desktopAvatarCard?.openModal()"
            class="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-white dark:bg-[#1C2128] border border-gray-200 dark:border-gray-600 shadow-sm flex items-center justify-center text-gray-500 dark:text-gray-400 hover:text-[#0070F2] hover:border-[#0070F2] transition-colors">
            <svg class="w-3 h-3" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125"/>
            </svg>
          </button>
        </div>
        <div class="min-w-0">
          <h1 class="text-base font-bold text-gray-900 dark:text-white truncate leading-tight">{{ org.name || 'Your organization' }}</h1>
          <a v-if="orgUrl" :href="'/' + orgUrl" target="_blank"
             class="inline-flex items-center gap-1 mt-0.5 text-xs text-[#0070F2] dark:text-blue-400 hover:underline font-mono">
            {{ APPLICATION_DOMAIN }}/{{ orgUrl }}
            <svg class="w-2.5 h-2.5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25"/>
            </svg>
          </a>
        </div>
      </div>
      <SaveStatusPill :status="saveStatus" />
    </div>

    <div class="space-y-4">

      <!-- Identity card -->
      <div class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden">
        <div class="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center gap-3">
          <div class="w-6 h-6 rounded-md bg-gray-100 dark:bg-gray-800 flex items-center justify-center flex-shrink-0">
            <svg class="w-3.5 h-3.5 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z" />
            </svg>
          </div>
          <div>
            <h2 class="text-sm font-semibold text-gray-900 dark:text-white leading-none">Identity</h2>
            <p class="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5 leading-none">Your organization's public-facing name and profile URL</p>
          </div>
        </div>
        <div class="px-6 py-5 space-y-4">
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label for="org_name" class="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Organization name</label>
              <input :value="org.name"
                     @focusout="updateOrgField"
                     name="organization[name]" id="org_name" type="text"
                     placeholder="Acme Inc."
                     class="w-full px-3.5 py-2.5 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-[#0d1117] text-gray-900 dark:text-gray-100 placeholder-gray-300 dark:placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-[#0070F2] focus:border-transparent transition-all" />
            </div>
            <div>
              <label for="org_url" class="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Organization URL</label>
              <div class="flex rounded-lg border transition-all"
                   :class="errors.url
                     ? 'border-[#EE3939] ring-2 ring-[#EE3939]/20'
                     : 'border-gray-200 dark:border-gray-700 focus-within:ring-2 focus-within:ring-[#0070F2] focus-within:border-transparent'">
                <span class="inline-flex items-center pl-3.5 pr-3 bg-gray-50 dark:bg-[#1C2128] border-r border-gray-200 dark:border-gray-700 text-xs text-gray-400 dark:text-gray-500 select-none flex-shrink-0 rounded-l-lg font-mono">
                  {{ APPLICATION_DOMAIN }}/
                </span>
                <input :value="orgUrl"
                       @input="validateURL"
                       @focusout="updateOrgField"
                       name="organization[url]" id="org_url" type="text" placeholder="your-org"
                       class="flex-1 px-3 py-2.5 text-sm bg-white dark:bg-[#0d1117] text-gray-900 dark:text-gray-100 placeholder-gray-300 border-0 focus:outline-none focus:ring-0 rounded-r-lg font-mono" />
              </div>
              <div class="mt-1.5 flex items-center gap-1.5">
                <template v-if="errors.url">
                  <svg class="w-3.5 h-3.5 text-[#EE3939] flex-shrink-0" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"/>
                  </svg>
                  <p class="text-xs text-[#EE3939]">{{ errors.url }}</p>
                </template>
                <template v-else-if="orgUrl">
                  <svg class="w-3.5 h-3.5 text-gray-300 dark:text-gray-600 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M13.19 8.688a4.5 4.5 0 0 1 1.242 7.244l-4.5 4.5a4.5 4.5 0 0 1-6.364-6.364l1.757-1.757m13.35-.622 1.757-1.757a4.5 4.5 0 0 0-6.364-6.364l-4.5 4.5a4.5 4.5 0 0 0 1.242 7.244"/>
                  </svg>
                  <a :href="'/' + orgUrl" target="_blank"
                     class="text-xs text-[#0070F2] hover:underline dark:text-blue-400 truncate font-mono">
                    {{ APPLICATION_URL }}/{{ orgUrl }}
                  </a>
                </template>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- About card -->
      <div class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden">
        <div class="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center gap-3">
          <div class="w-6 h-6 rounded-md bg-gray-100 dark:bg-gray-800 flex items-center justify-center flex-shrink-0">
            <svg class="w-3.5 h-3.5 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.129.166 2.27.293 3.423.379.35.026.67.21.865.501L12 21l2.755-4.133a1.14 1.14 0 0 1 .865-.501 48.172 48.172 0 0 0 3.423-.379c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0 0 12 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018Z"/>
            </svg>
          </div>
          <div>
            <h2 class="text-sm font-semibold text-gray-900 dark:text-white leading-none">About</h2>
            <p class="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5 leading-none">Shown on your organization's public profile page</p>
          </div>
        </div>
        <div class="px-6 py-5">
          <label for="org_bio" class="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Bio</label>
          <textarea :value="org.bio"
                    @focusout="updateOrgField"
                    name="organization[bio]" id="org_bio" rows="4"
                    placeholder="Tell people what your organization does"
                    class="w-full px-3.5 py-3 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-[#0d1117] text-gray-900 dark:text-gray-100 placeholder-gray-300 dark:placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-[#0070F2] focus:border-transparent transition-all resize-none leading-relaxed"></textarea>
        </div>
      </div>

      <!-- Preferences card -->
      <div class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden">
        <div class="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center gap-3">
          <div class="w-6 h-6 rounded-md bg-gray-100 dark:bg-gray-800 flex items-center justify-center flex-shrink-0">
            <svg class="w-3.5 h-3.5 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M10.343 3.94c.09-.542.56-.94 1.11-.94h1.093c.55 0 1.02.398 1.11.94l.149.894c.07.424.384.764.78.93.398.164.855.142 1.205-.108l.737-.527a1.125 1.125 0 0 1 1.45.12l.773.774c.39.389.44 1.002.12 1.45l-.527.737c-.25.35-.272.806-.107 1.204.165.397.505.71.93.78l.893.15c.543.09.94.559.94 1.109v1.094c0 .55-.397 1.02-.94 1.11l-.894.149c-.424.07-.764.383-.929.78-.165.398-.143.854.107 1.204l.527.738c.32.447.269 1.06-.12 1.45l-.774.773a1.125 1.125 0 0 1-1.449.12l-.738-.527c-.35-.25-.806-.272-1.203-.107-.398.165-.71.505-.781.929l-.149.894c-.09.542-.56.94-1.11.94h-1.094c-.55 0-1.019-.398-1.11-.94l-.148-.894c-.071-.424-.384-.764-.781-.93-.398-.164-.854-.142-1.204.108l-.738.527c-.447.32-1.06.269-1.45-.12l-.773-.774a1.125 1.125 0 0 1-.12-1.45l.527-.737c.25-.35.272-.806.108-1.204-.165-.397-.506-.71-.93-.78l-.894-.15c-.542-.09-.94-.56-.94-1.109v-1.094c0-.55.398-1.02.94-1.11l.894-.149c.424-.07.765-.383.93-.78.165-.398.143-.854-.108-1.204l-.526-.738a1.125 1.125 0 0 1 .12-1.45l.773-.773a1.125 1.125 0 0 1 1.45-.12l.737.527c.35.25.807.272 1.204.107.397-.165.71-.505.78-.929l.15-.894Z"/>
              <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"/>
            </svg>
          </div>
          <div>
            <h2 class="text-sm font-semibold text-gray-900 dark:text-white leading-none">Preferences</h2>
            <p class="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5 leading-none">Control what visitors see on your pages</p>
          </div>
        </div>
        <div class="px-6 py-4 flex items-center justify-between gap-6 border-b border-gray-50 dark:border-gray-800/60">
          <div class="min-w-0 flex-1">
            <p class="text-sm font-medium text-gray-800 dark:text-gray-200 leading-snug">{{ I18N.website_content.account_settings.show_msg_btn_on_profile_page }}</p>
            <p class="text-xs text-gray-400 dark:text-gray-500 mt-0.5 leading-snug">{{ I18N.website_content.account_settings.subheading1 }}</p>
          </div>
          <ToggleSwitch :model-value="org.show_message_button_on_profile" @change="togglePreference('show_message_button_on_profile', $event)" />
        </div>
        <div class="px-6 py-4 flex items-center justify-between gap-6">
          <div class="min-w-0 flex-1">
            <p class="text-sm font-medium text-gray-800 dark:text-gray-200 leading-snug">{{ I18N.website_content.account_settings.show_msg_btn_on_events_page }}</p>
            <p class="text-xs text-gray-400 dark:text-gray-500 mt-0.5 leading-snug">{{ I18N.website_content.account_settings.subheading2 }}</p>
          </div>
          <ToggleSwitch :model-value="org.show_message_button_on_events" @change="togglePreference('show_message_button_on_events', $event)" />
        </div>
      </div>

      <!-- Social links link-out card -->
      <router-link :to="`/organizations/${org.url}/profile/social_links`" data-turbo-prefetch="false"
        class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden hover:border-[#0070F2] dark:hover:border-blue-600 transition-colors group flex items-center gap-4 px-6 py-4">
        <div class="w-9 h-9 rounded-lg bg-[#EBF5FF] dark:bg-blue-900/20 flex items-center justify-center flex-shrink-0">
          <svg class="w-5 h-5 text-[#0070F2] dark:text-blue-400" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M13.19 8.688a4.5 4.5 0 0 1 1.242 7.244l-4.5 4.5a4.5 4.5 0 0 1-6.364-6.364l1.757-1.757m13.35-.622 1.757-1.757a4.5 4.5 0 0 0-6.364-6.364l-4.5 4.5a4.5 4.5 0 0 0 1.242 7.244"/>
          </svg>
        </div>
        <div class="flex-1 min-w-0">
          <h2 class="text-sm font-semibold text-gray-900 dark:text-white leading-snug group-hover:text-[#0070F2] dark:group-hover:text-blue-400 transition-colors">Social Links</h2>
          <p class="text-xs text-gray-400 dark:text-gray-500 mt-0.5">Website, social profiles, and visibility</p>
        </div>
        <svg class="w-4 h-4 text-gray-300 dark:text-gray-600 flex-shrink-0 group-hover:text-[#0070F2] transition-colors" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="m9 18 6-6-6-6"/>
        </svg>
      </router-link>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { orgStore } from 'controllers/vue_components/organizations/store'
import { useRequest } from 'composables/useRequest.js'
import { useConstants } from 'composables/useConstants.js'
import FlashNotifications from 'controllers/vue_components/flash_toast'
import ToggleSwitch from 'controllers/vue_components/settings/shared/ToggleSwitch'
import SaveStatusPill from 'controllers/vue_components/settings/shared/SaveStatusPill'
import AvatarUploadCard from 'controllers/vue_components/settings/shared/AvatarUploadCard'

const flash = ref(null)
const { axiosRequest } = useRequest(flash)
const { I18N, APPLICATION_URL, APPLICATION_DOMAIN } = useConstants()
const store = orgStore()

const org = computed(() => store.current_org)

const orgUrl = ref('')
const errors = ref({})
const saveStatus = ref('idle')
let urlDebounceTimer = null

const mobileAvatarCard = ref(null)
const desktopAvatarCard = ref(null)

onMounted(() => {
  orgUrl.value = org.value.url
})

function onAvatarUpdated(newOrgData) {
  store.$patch({ current_org: newOrgData })
}

function onAvatarError(message) {
  flash.value?.show(message, 'alert')
}

function handleNotifications(response) {
  if (response.js_error === false) {
    if (!response.response.success) {
      flash.value?.show(response.response.message, 'alert')
    }
  } else {
    flash.value?.show(response.error?.data?.message || 'Something went wrong', 'alert')
  }
}

async function markSaved() {
  saveStatus.value = 'saved'
  setTimeout(() => { if (saveStatus.value === 'saved') saveStatus.value = 'idle' }, 2500)
}

async function updateOrgField(e) {
  const [, fieldName] = e.target.name.split('[').map((p) => p.replace(']', ''))
  const fieldValue = e.target.value
  if (org.value[fieldName] === fieldValue) return

  saveStatus.value = 'saving'
  const response = await store.updateCurrentOrg({ organization: { [fieldName]: fieldValue } })
  await markSaved()
  handleNotifications(response)

  if (response.js_error === false && fieldName === 'url') {
    history.pushState(null, null, `/organizations/${fieldValue}/profile`)
  }
}

async function togglePreference(fieldName, newValue) {
  saveStatus.value = 'saving'
  const response = await store.updateCurrentOrg({ organization: { [fieldName]: newValue } })
  await markSaved()
  handleNotifications(response)
}

function validateURL(e) {
  orgUrl.value = e.target.value.replace(/\s+/g, '-').toLowerCase()
  clearTimeout(urlDebounceTimer)
  urlDebounceTimer = setTimeout(async () => {
    const url = orgUrl.value
    if (!url) {
      errors.value.url = I18N.validations.url_cant_be_blank
      return
    }
    delete errors.value.url
    const res = await axiosRequest('POST', '/api/v1/organizations/validate_url_availability', { organization_id: org.value.id, organization: { url } }, false)
    if (res.js_error === false && res.response.success) {
      if (res.response.data.available === false) {
        errors.value.url = res.response.message
      }
    }
  }, 400)
}
</script>
